import Text from 'components/Text/Text';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IItemForGetShortInfoWithStatus, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useArchivedMultiSelect } from 'hooks/useArchivedMultiSelect';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import FormSelect from 'components/Select/FormSelect';
import { ISelectOption } from 'components/Select/types';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	setIDsRestaurant: (value: string[]) => void;
	IDsSelectedRestaurant: string[];
	restaurantsToExclude: string[];
	inputName: string;
	inputLabel: string;
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	setIDsRestaurant,
	IDsSelectedRestaurant,
	restaurantsToExclude,
	inputName,
	inputLabel,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const [searchString, setSearchString] = useState<string>('');

	const { showArchived, toggleShowArchived, visibleItems, options } = useArchivedMultiSelect({
		items: data,
		entityType: 'restaurants',
		selectedIds: IDsSelectedRestaurant,
	});

	const filteredData = useMemo(() => {
		return searchString
			? options.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: options;
	}, [searchString, options]);

	const accessibleRestaurantIds = useMemo(
		() =>
			loggedUser.restaurants
				? loggedUser.restaurants
						.filter((restaurant) => !restaurantsToExclude.includes(restaurant.id))
						.map((el) => el.id)
				: [],
		[loggedUser, restaurantsToExclude],
	);

	const mappedOptions = useMemo<ISelectOption[]>(() => {
		return filteredData.map((el) => ({
			...el,
			disabled: !hasAccess && !accessibleRestaurantIds.some((id) => id === el.id),
		}));
	}, [filteredData, loggedUser, hasAccess]);

	const handleSelectAllData = useCallback(() => {
		if (hasAccess) {
			if (visibleItems.length === IDsSelectedRestaurant.length) {
				setIDsRestaurant([]);
			} else {
				const IDsOfAllRestaurants = visibleItems.map((el) => el.id);
				setIDsRestaurant(IDsOfAllRestaurants);
			}
		} else {
			if (accessibleRestaurantIds.every((el) => IDsSelectedRestaurant.includes(el))) {
				const filteredIDsSelectedRestaurant = IDsSelectedRestaurant.filter(
					(el) => !accessibleRestaurantIds.includes(el),
				);
				setIDsRestaurant(filteredIDsSelectedRestaurant);
			} else {
				const filteredAccessibleRestaurantIds = accessibleRestaurantIds.filter(
					(el) => !IDsSelectedRestaurant.includes(el),
				);
				setIDsRestaurant([...IDsSelectedRestaurant, ...filteredAccessibleRestaurantIds]);
			}
		}
	}, [setIDsRestaurant, hasAccess, visibleItems, IDsSelectedRestaurant, loggedUser]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			const getIsCheckedByHasAccess = () => {
				return !!(visibleItems.length && visibleItems.length === IDsSelectedRestaurant.length);
			};

			const getIsCheckedByWithoutAccess = () => {
				return accessibleRestaurantIds.every((el) => IDsSelectedRestaurant.includes(el));
			};

			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={hasAccess ? getIsCheckedByHasAccess() : getIsCheckedByWithoutAccess()}
							disabled={hasAccess ? !visibleItems.length : !loggedUser.restaurants?.length}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleItems,
			loggedUser,
			IDsSelectedRestaurant,
			handleSelectAllData,
			hasAccess,
			setSearchString,
			searchString,
			showArchived,
			toggleShowArchived,
		],
	);

	return (
		<FormSelect
			testId="MultipleSelect"
			name={inputName}
			label={inputLabel}
			options={mappedOptions}
			disabled={!data.length}
			onChange={setIDsRestaurant}
			mode="multiple"
			counterValue={IDsSelectedRestaurant.length}
			dropdownRender={renderSelectMenu}
		/>
	);
};

export default React.memo(MultipleSelect);
